import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Barcode,
  CheckCircle2,
  Database,
  Gauge,
  Snowflake,
  type LucideIcon,
} from "lucide-react";
import { BarChart } from "../components/ui/BarChart";
import { BarList } from "../components/ui/BarList";
import { Badge } from "../components/ui/Badge";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { DonutChart } from "../components/ui/DonutChart";
import { Input } from "../components/ui/Input";
import { Pagination } from "../components/ui/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import { fmtNumber, fmtPercent, fmtRelative } from "../lib/format";
import type {
  BarcodeLookupStatus,
  BarcodeProduct,
  BarcodeSource,
  BarcodeStatsResponse,
} from "../lib/types";

const PAGE_SIZE = 25;

const SOURCES: BarcodeSource[] = [
  "fatsecret",
  "nutritionix",
  "openfoodfacts",
  "usda",
  "upcitemdb",
];

const STATUS_VARIANT: Record<
  BarcodeLookupStatus,
  "default" | "neutral" | "success" | "error" | "warning"
> = {
  found: "success",
  missing_ingredients: "warning",
  not_found: "neutral",
  error: "error",
};

type ListResponse = {
  items: Array<BarcodeProduct>;
  total: number;
  page: number;
  pageSize: number;
};

export function BarcodesPage() {
  const [rawQ, setRawQ] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<BarcodeLookupStatus | "">(
    "",
  );
  const [sourceFilter, setSourceFilter] = useState<BarcodeSource | "">("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setQ(rawQ.trim()), 250);
    return () => clearTimeout(t);
  }, [rawQ]);

  useEffect(() => {
    setPage(1);
  }, [q, statusFilter, sourceFilter]);

  const stats = useQuery<BarcodeStatsResponse>({
    queryKey: ["admin-barcodes-stats"],
    queryFn: () =>
      api<BarcodeStatsResponse>("/v1/admin/barcodes/stats?days=30"),
  });

  const listPath = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (statusFilter) params.set("status", statusFilter);
    if (sourceFilter) params.set("source", sourceFilter);
    if (q) params.set("q", q);
    return `/v1/admin/barcodes?${params.toString()}`;
  }, [page, statusFilter, sourceFilter, q]);

  const list = useQuery<ListResponse>({
    queryKey: ["admin-barcodes", listPath],
    queryFn: () => api<ListResponse>(listPath),
    placeholderData: (prev) => prev,
  });

  const totalPages = list.data
    ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE))
    : 1;
  const from =
    list.data && list.data.items.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to =
    list.data && list.data.items.length > 0
      ? from + list.data.items.length - 1
      : 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Barcodes
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Cached UPC products + per-source resolution. Cache hit counters accrue
          from the 0015 migration onward.
        </p>
      </header>

      {stats.error && (
        <Callout title="Failed to load stats" variant="error">
          {(stats.error as Error).message}
        </Callout>
      )}

      {stats.isLoading && !stats.data ? (
        <Skeleton rows={4} />
      ) : stats.data ? (
        <BarcodesDashboard data={stats.data} />
      ) : null}

      <Card>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          Cached barcodes
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          One row per barcode (winner across all sources).
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="block text-xs text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as BarcodeLookupStatus | "")
              }
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-50"
            >
              <option value="">All</option>
              <option value="found">Found</option>
              <option value="missing_ingredients">Missing ingredients</option>
              <option value="not_found">Not found</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) =>
                setSourceFilter(e.target.value as BarcodeSource | "")
              }
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-50"
            >
              <option value="">All</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500">
              Barcode / product
            </label>
            <Input
              type="search"
              value={rawQ}
              onChange={(e) => setRawQ(e.target.value)}
              placeholder="e.g. 0123 or cereal"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
      </Card>

      {list.error && (
        <Callout title="Failed to load list" variant="error">
          {(list.error as Error).message}
        </Callout>
      )}

      {list.isLoading && !list.data ? (
        <Skeleton rows={5} />
      ) : !list.data || list.data.items.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500">
            No barcodes match the current filters.
          </p>
        </Card>
      ) : (
        <>
          <TableRoot>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Barcode</TableHeaderCell>
                  <TableHeaderCell>Product</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell className="text-right">Ing.</TableHeaderCell>
                  <TableHeaderCell className="text-right">Hits</TableHeaderCell>
                  <TableHeaderCell>Fetched</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.items.map((row) => (
                  <TableRow key={row.barcode}>
                    <TableCell className="font-mono text-xs">
                      {row.barcode}
                    </TableCell>
                    <TableCell className="text-gray-900 dark:text-gray-50">
                      <div>{row.product_name ?? "—"}</div>
                      {row.brand && (
                        <div className="text-xs text-gray-500">{row.brand}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">{row.source}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status]}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.ingredients?.length ?? 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNumber(row.hit_count)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-gray-500">
                      {fmtRelative(row.fetched_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableRoot>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={list.data.total}
            from={from}
            to={to}
            busy={list.isFetching}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function BarcodesDashboard({ data }: { data: BarcodeStatsResponse }) {
  const cache = data.cache;
  const status = data.status_breakdown;
  const totalStatus =
    status.found + status.missing_ingredients + status.not_found + status.error;
  const foundPct =
    totalStatus > 0
      ? (status.found + status.missing_ingredients) / totalStatus
      : null;

  const sourceMix = data.sources
    .filter((s) => s.attempts > 0)
    .map((s) => ({
      name: s.source,
      Found: s.found,
      "Missing ing.": s.missing_ingredients,
      "Not found": s.not_found,
      Error: s.error,
    }));

  const missRates = data.sources
    .filter((s) => s.miss_rate != null)
    .map((s) => ({
      name: s.source,
      value: Math.round((s.miss_rate ?? 0) * 1000) / 10,
    }));

  const statusDonut = [
    { name: "Found", value: status.found },
    { name: "Missing ing.", value: status.missing_ingredients },
    { name: "Not found", value: status.not_found },
    { name: "Error", value: status.error },
  ].filter((r) => r.value > 0);

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          icon={Barcode}
          label="Lookup requests"
          value={fmtNumber(data.usage.count)}
          sub={
            data.usage.failure_rate != null
              ? `${fmtPercent(data.usage.failure_rate)} not found/error`
              : "last 30d"
          }
          tone={
            data.usage.failure_rate != null && data.usage.failure_rate > 0.4
              ? "warn"
              : undefined
          }
        />
        <KpiCard
          icon={Database}
          label="Cached barcodes"
          value={fmtNumber(cache.products_total)}
          sub={`${fmtNumber(cache.products_fresh)} fresh · ${fmtNumber(cache.products_expired)} expired`}
        />
        <KpiCard
          icon={Gauge}
          label="Cache hit rate"
          value={
            cache.cache_hit_rate != null
              ? fmtPercent(cache.cache_hit_rate)
              : "—"
          }
          sub={`${fmtNumber(cache.cache_hits)} hits of ${fmtNumber(cache.cache_attempts)} attempts`}
          tone={
            cache.cache_hit_rate != null && cache.cache_hit_rate >= 0.5
              ? "ok"
              : undefined
          }
        />
        <KpiCard
          icon={Snowflake}
          label="Source rows"
          value={fmtNumber(cache.source_results_total)}
          sub={`${fmtNumber(cache.source_results_fresh)} fresh`}
        />
        <KpiCard
          icon={CheckCircle2}
          label="% Found"
          value={foundPct != null ? fmtPercent(foundPct) : "—"}
          sub={`${fmtNumber(status.found)} found · ${fmtNumber(status.not_found)} miss`}
          tone={foundPct != null && foundPct >= 0.5 ? "ok" : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Per-source resolution
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Stacked count of outcomes per upstream.
          </p>
          {sourceMix.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No source results yet.
            </p>
          ) : (
            <BarChart
              data={sourceMix}
              index="name"
              categories={["Found", "Missing ing.", "Not found", "Error"]}
              colors={["emerald", "amber", "gray", "pink"]}
              type="stacked"
              valueFormatter={fmtNumber}
              yAxisWidth={56}
              className="mt-6 h-64"
            />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:items-stretch">
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Status breakdown
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            All cached barcodes.
          </p>
          {statusDonut.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No cached barcodes yet.
            </p>
          ) : (
            <DonutChart
              data={statusDonut}
              category="name"
              value="value"
              colors={["emerald", "amber", "gray", "pink"]}
              valueFormatter={fmtNumber}
              label={fmtNumber(totalStatus)}
              showLabel
              className="mx-auto mt-6 h-44 w-44"
            />
          )}
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Per-source miss rate
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Higher = source rarely had the product. Values shown as %.
          </p>
          {missRates.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No source results yet.
            </p>
          ) : (
            <BarList
              data={missRates}
              valueFormatter={(v) => `${v}%`}
              className="mt-6"
            />
          )}
        </Card>
      </div>

      {data.recent.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Recent fetches
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Last {data.recent.length} barcode entries by fetch time.
          </p>
          <ul className="mt-4 divide-y divide-stone-200 dark:divide-white/10">
            {data.recent.map((row) => (
              <li
                key={row.barcode}
                className="flex items-center justify-between gap-4 py-2.5 text-sm"
              >
                <div className="min-w-0 flex-1 truncate pr-3">
                  <p className="truncate text-gray-900 dark:text-gray-50">
                    {row.product_name ?? row.barcode}
                  </p>
                  {row.brand && (
                    <p className="truncate text-xs text-gray-500">
                      {row.brand}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-gray-500">
                  <Badge variant="default">{row.source}</Badge>
                  <Badge variant={STATUS_VARIANT[row.status]}>
                    {row.status}
                  </Badge>
                  <span>{fmtRelative(row.fetched_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "warn";
}) {
  const valueColor =
    tone === "ok"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-gray-900 dark:text-gray-50";
  return (
    <Card className="min-h-[112px]">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Icon size={14} strokeWidth={2} />
        {label}
      </div>
      <p className={`mt-2 text-2xl font-semibold tabular-nums ${valueColor}`}>
        {value}
      </p>
      {sub && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{sub}</p>
      )}
    </Card>
  );
}

function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-16 w-full animate-pulse rounded-md bg-gray-100 dark:bg-gray-900"
        />
      ))}
    </div>
  );
}
